import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminTenantAPI from '../../api/admin/tenants.js';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import './AdminTenants.css';

const AdminTenants = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await AdminTenantAPI.getTenants();
      setTenants(response.tenants || []);
    } catch (err) {
      setError('Failed to load tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (tenant) => {
    navigate(`/admin/tenants/${tenant.id}`);
  };

  const handleImpersonate = async (tenant) => {
    setSelectedTenant(tenant);
    setShowUserModal(true);
    setLoadingUsers(true);
    
    try {
      // Load users for this tenant
      const response = await AdminTenantAPI.getTenantUsers(tenant.id);
      setTenantUsers(response.users || []);
    } catch (err) {
      console.error('Error loading tenant users:', err);
      setError('Failed to load tenant users');
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserImpersonation = async (user) => {
    setImpersonating(true);
    
    try {
      console.log('Starting impersonation for user:', user);
      
      const reason = prompt('Please enter reason for impersonation:');
      if (!reason) {
        console.log('Impersonation cancelled - no reason provided');
        setImpersonating(false);
        return;
      }

      console.log('Calling AdminTenantAPI.startImpersonation with:', { userId: user.id, reason });
      const result = await AdminTenantAPI.startImpersonation(user.id, reason);
      console.log('Impersonation result:', result);
      
      // Check if impersonation data was stored
      const storedToken = localStorage.getItem('impersonation_token');
      const storedUser = localStorage.getItem('impersonation_user');
      console.log('Stored impersonation data:', { token: storedToken ? 'Present' : 'Missing', user: storedUser ? 'Present' : 'Missing' });
      
      // Close modal and reload page to show impersonation status
      setShowUserModal(false);
      setSelectedTenant(null);
      
      alert('Impersonation started successfully! Page will reload to show banner.');
      window.location.reload(); // Refresh to show impersonation banner
      
    } catch (err) {
      console.error('Error starting impersonation:', err);
      alert('Failed to start impersonation: ' + (err.message || 'Unknown error'));
    } finally {
      setImpersonating(false);
    }
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedTenant(null);
    setTenantUsers([]);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-tenants-loading">
          <div className="loading-spinner"></div>
          <p>Loading tenants...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-tenants-error">
          <h2>Error Loading Tenants</h2>
          <p>{error}</p>
          <button onClick={loadTenants}>Retry</button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-tenants">
        <div className="page-header">
          <h1>Tenant Management</h1>
          <p>Manage all tenant accounts and their authentication status</p>
        </div>

        <div className="tenants-grid">
          {tenants.map(tenant => (
            <div key={tenant.id} className="tenant-card">
              <div className="tenant-header">
                <h3>{tenant.name}</h3>
                <div className={`status-badge ${tenant.status}`}>
                  {tenant.status}
                </div>
              </div>
              
              <div className="tenant-details">
                <div className="detail-row">
                  <span className="label">Auth Method:</span>
                  <span className={`auth-method ${tenant.auth_method}`}>
                    {tenant.auth_method === 'stytch' ? '🔐 Stytch' : '🔑 Legacy'}
                  </span>
                </div>
                
                <div className="detail-row">
                  <span className="label">Users:</span>
                  <span>{tenant.user_count}</span>
                </div>
                
                <div className="detail-row">
                  <span className="label">Created:</span>
                  <span>{new Date(tenant.created_at).toLocaleDateString()}</span>
                </div>
                
                {tenant.stytch_organization_id && (
                  <div className="detail-row">
                    <span className="label">Stytch Org:</span>
                    <span className="stytch-org">{tenant.stytch_organization_id}</span>
                  </div>
                )}
              </div>
              
              <div className="tenant-actions">
                <button 
                  className="btn-primary"
                  onClick={() => handleViewDetails(tenant)}
                >
                  View Details
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => handleImpersonate(tenant)}
                  disabled={tenant.user_count === 0}
                >
                  Impersonate
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* User Selection Modal */}
        {showUserModal && selectedTenant && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Select User to Impersonate</h2>
                <p>Choose a user from <strong>{selectedTenant.name}</strong> to impersonate</p>
                <button className="modal-close" onClick={closeUserModal}>×</button>
              </div>
              
              <div className="modal-body">
                {loadingUsers ? (
                  <div className="loading-users">
                    <div className="loading-spinner"></div>
                    <p>Loading users...</p>
                  </div>
                ) : tenantUsers.length === 0 ? (
                  <div className="no-users">
                    <p>No users found for this tenant.</p>
                  </div>
                ) : (
                  <div className="users-list">
                    {tenantUsers.map(user => (
                      <div key={user.id} className="user-item">
                        <div className="user-info">
                          <div className="user-name">{user.name}</div>
                          <div className="user-email">{user.email}</div>
                          <div className="user-role">Role: {user.role}</div>
                          {user.last_login && (
                            <div className="user-last-login">
                              Last login: {new Date(user.last_login).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <button
                          className="btn-impersonate"
                          onClick={() => handleUserImpersonation(user)}
                          disabled={impersonating}
                        >
                          {impersonating ? 'Starting...' : 'Impersonate'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <button onClick={closeUserModal} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTenants; 